/**
 * Centralized API error handling utilities.
 *
 * Provides consistent error handling for API calls across the application.
 * Handles different error types (network errors, HTTP errors) and displays
 * appropriate user-friendly messages via toast notifications.
 *
 * @module apiErrorHandler
 */

import { toast } from 'sonner';
import { logger } from './logger';

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
}

/**
 * Determines if an error is a network error.
 *
 * Checks if the error is a TypeError related to network/fetch operations,
 * which typically indicates a connection problem or CORS issue.
 *
 * @param error - Error object to check
 * @returns True if error is a network error, false otherwise
 */
function isNetworkError(error: unknown): boolean {
  return (
    error instanceof TypeError &&
    (error.message.includes('fetch') || error.message.includes('network'))
  );
}

/**
 * Determines if an error is an HTTP error response.
 *
 * Type guard that checks if the error object has a status property indicating
 * an HTTP error response (non-2xx status code).
 *
 * @param error - Error object to check
 * @returns True if error is an HTTP error with status code, false otherwise
 */
function isHttpError(error: unknown): error is { status: number; message?: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof (error as { status: unknown }).status === 'number'
  );
}

/**
 * Extracts error message from various error types.
 *
 * Safely extracts a human-readable error message from different error object
 * types (Error, string, HTTP error, etc.).
 *
 * @param error - Error object of unknown type
 * @returns Extracted error message string, or default message if extraction fails
 */
function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (isHttpError(error) && error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

/**
 * Handles API errors with consistent logging and user notifications.
 *
 * Centralized error handling that logs errors for debugging and displays
 * appropriate user-friendly messages via toast notifications. Handles different
 * error types (network errors, HTTP errors) with specific messages for common
 * status codes.
 *
 * @param error - The error to handle (can be Error, HTTP error, or unknown type)
 * @param defaultMessage - Default error message to show to user if error type cannot be determined
 * @param context - Additional context for logging (e.g., function name where error occurred)
 * @returns ApiError object containing:
 *   - `message`: Human-readable error message
 *   - `status`: HTTP status code (if HTTP error)
 *   - `code`: Error code string (e.g., 'NETWORK_ERROR', 'UNAUTHORIZED')
 *
 * @example
 * ```typescript
 * try {
 *   await apiCall();
 * } catch (error) {
 *   const apiError = handleApiError(error, 'Operation failed', 'myFunction');
 *   // Error is logged and toast notification shown
 * }
 * ```
 */
export function handleApiError(
  error: unknown,
  defaultMessage: string,
  context?: string,
  options?: { readonly suppressToast?: boolean },
): ApiError {
  const errorMessage = extractErrorMessage(error);
  const suppressToast = options?.suppressToast ?? false;

  // Log error for debugging
  const logMessage = context ? `Error [${context}]` : 'Error';
  logger.error(logMessage, error, { context: context || undefined });

  const showToast = (message: string) => {
    if (!suppressToast) {
      toast.error(message);
    }
  };

  // Determine error type and show appropriate toast
  if (isNetworkError(error)) {
    showToast('Network error. Please check your connection and try again.');
    return {
      message: 'Network error',
      code: 'NETWORK_ERROR',
    };
  }

  if (isHttpError(error)) {
    // Handle specific HTTP status codes
    switch (error.status) {
      case 401:
        showToast('Authentication required. Please log in again.');
        return {
          message: 'Authentication required',
          status: 401,
          code: 'UNAUTHORIZED',
        };
      case 403:
        showToast('You do not have permission to perform this action.');
        return {
          message: 'Permission denied',
          status: 403,
          code: 'FORBIDDEN',
        };
      case 404:
        showToast('The requested resource was not found.');
        return {
          message: 'Resource not found',
          status: 404,
          code: 'NOT_FOUND',
        };
      case 422:
        showToast('Invalid data provided. Please check your input.');
        return {
          message: 'Validation error',
          status: 422,
          code: 'VALIDATION_ERROR',
        };
      case 500:
      case 502:
      case 503:
        showToast('Server error. Please try again later.');
        return {
          message: 'Server error',
          status: error.status,
          code: 'SERVER_ERROR',
        };
      default:
        showToast(defaultMessage);
        return {
          message: errorMessage,
          status: error.status,
          code: 'HTTP_ERROR',
        };
    }
  }

  // Generic error handling
  showToast(defaultMessage);
  return {
    message: errorMessage,
    code: 'UNKNOWN_ERROR',
  };
}

/**
 * Wraps an API call with standardized error handling.
 *
 * Convenience wrapper that executes an async function and automatically handles
 * any errors using the centralized error handling system. Returns null on error
 * to allow for graceful degradation.
 *
 * @param apiCall - The async function to execute
 * @param defaultErrorMessage - Default error message to show to user if call fails
 * @param context - Context for logging (e.g., function name)
 * @returns Promise resolving to the result of the API call, or null if an error occurred
 *
 * @example
 * ```typescript
 * const data = await withErrorHandling(
 *   () => fetchData(),
 *   'Failed to load data',
 *   'loadData'
 * );
 * if (data) {
 *   // Use data
 * }
 * ```
 */
export async function withErrorHandling<T>(
  apiCall: () => Promise<T>,
  defaultErrorMessage: string,
  context?: string,
): Promise<T | null> {
  try {
    return await apiCall();
  } catch (error) {
    handleApiError(error, defaultErrorMessage, context);
    return null;
  }
}

